import { ImageObject } from '../../../data/image/ImageObject';
import { StructureSetObject } from '../../../data/structure/StructureSetObject';
import { RenderStorage } from '../../../core/render/RenderStorage';
import {
    ImageRenderer,
    ImageRenderingOptions,
} from '../../../core/render/renderers/ImageRenderer';
import { Layer } from '../Layer';
import { gl } from '../../../Engine';
import { ReadonlyVec2 } from 'gl-matrix';
import {
    ContourRenderer,
    ContourRenderOptions,
} from '../../../core/render/renderers/ContourRenderer';
import { DoseRenderer } from '../../../core/render/renderers/DoseRenderer';
import { DoseDistribution } from '../../../data/dose/DoseDistribution';
import { Environment } from '~/engine/data/Environment';
import { Modality } from '~/domain/dicom/modality';

/**
 * {@link Filter} controls how data of type T is rendered on a {@link Layer}.
 */
export abstract class Filter<T> {
    /**
     * Readonly variable that holds a reference to the parent {@link Layer}.
     * This value should never be modified!
     */
    protected readonly parent: Layer<T>;

    /**
     * Possible {@link WebGLTexture texture} used to render the contents on.\
     * This is then rendered on the {@link Layer} within a post-processing step.
     */
    protected readonly texture?: WebGLTexture;

    protected readonly env: Environment;

    /**
     * Filter constructor with size parameter.
     * All filters will need to know the size of the underlying Layer/View
     * @param size The size of the Layer/View
     */
    public constructor(parent: Layer<T>, env: Environment) {
        this.parent = parent;
        this.env = env;
    }

    public update(targets: T[], shouldUpdateContent: boolean): boolean {
        if (shouldUpdateContent) {
            //if Filter has post-processing, updateContent will only update the Filter's texture
            //and not the Layer's texture. Layer's texture is updated by the post-processing.
            this.updateContent(targets);
        }
        if (this.postProcessing) {
            this.postProcessing(targets);
        }
        return true;
    }

    /**
     * Method to {@link gl.framebufferTexture2D bind} {@link texture} on the currently bound {@link FrameBufferObject}.
     * @param texture {@link WebGLTexture} that is to be {@link gl.framebufferTexture2D bound} on the currently active {@link FrameBufferObject}.
     */
    protected bindTexture(texture: WebGLTexture): void {
        gl.framebufferTexture2D(
            gl.FRAMEBUFFER,
            gl.COLOR_ATTACHMENT0,
            gl.TEXTURE_2D,
            texture,
            0
        );
    }

    /**
     * Method to delete this Filter.
     * Filter may contain texture data that needs to be de-allocated when the Filter is no longer needed.
     */
    public delete(): void {
        if (this.texture) {
            gl.deleteTexture(this.texture);
        }
    }

    /**
     * Method to update either the {@link Layer Layer's} texture directly or the texture of this {@link Filter}.\
     * Depending on whether this {@link Filter} offers {@link Filter.postProcessing postProcessing} or not.
     *
     * If this {@link Filter} uses post-processing, the content is rendered onto {@link Layer} within the post-processing step.
     *
     * @param targets All the objects of the datatype this Filter consumes currently available in the environment
     */
    public abstract updateContent(targets: T[]): void;

    /**
     * Method that updates the content of the filter.\
     * This is used to add post-processing effects.
     *
     * {@link updateContent} is used first to update the content of the Filter.
     *
     * For example in the case of {@link CheckerboardFilter} we have two textures,\
     * one rendered with the contents of scan A and the other one with the contents of scan B.\
     * When user performs action that alters the {@link ImageLayer}, we update those underlying textures.\
     * And then in the {@link postProcessing}-method we combine those two textures into one as required by the {@link Filter}.
     *
     * @param targets All the objects of the datatype this {@link Filter} consumes currently available in the {@link Environment}.
     */
    public abstract postProcessing?(targets: T[]): void;

    /**
     * Optional method to resize the {@link Filter}.
     *
     * @param size The new {@link ReadonlyVec2} pixelsize of the {@link Filter}. This is called by this {@link Filter Filter's} {@link Filter.parent parent}.
     */
    public resize?(size: ReadonlyVec2): Filter<T>;
}

/**
 * The most basic {@link ImageFilter} that consumes objects of type {@link ImageObject} and applies no post-processing.\
 * All it does is render the {@link ImageObject Images} on the {@link Layer} underneath.
 */
export class ImageFilter extends Filter<ImageObject> {
    public constructor(parent: Layer<ImageObject>, env: Environment) {
        super(parent, env);
    }

    /**
     * Method to delete the WebGL data stored inside this {@link Filter}.\
     * This method should be called when the {@link Filter} is no more needed.
     */
    public delete(): void {
        //The default image filter has nothing to delete
    }

    /**
     * Method to update the contents of this {@link Filter}.\
     * Images are retrieved directly from the environment rather than using the targets parameter,
     * since the system explicitly supports only primary and secondary images with specific fusion modes.
     *
     * Always binds and clears the texture first so that stale image data is removed from the
     * GPU framebuffer when images are unloaded.
     *
     * @param _images Unused. Required by {@link Filter} interface but images are retrieved from environment.
     */
    public updateContent(_images: ImageObject[]): void {
        // Always bind and clear the texture first, even if there's no image to render.
        // This ensures stale image data is cleared from the GPU framebuffer on unload.
        this.bindTexture(this.texture ? this.texture : this.parent.texture);
        gl.clear(gl.COLOR_BUFFER_BIT);

        const imageRenderer = RenderStorage.getRenderer(ImageRenderer);
        //imageRenderer.renderOriginal(this.parent.getView(), images)
        // Inspect both env slots, not just the `images` parameter — PET
        // may sit on either side of the fusion and the renderer must
        // know to engage the colormap shader in either case.
        const ifPetImageFused = [this.env.image, this.env.secondaryImage].some(
            (image) => image?.parent.seriesMetadata.modality === Modality.PT
        );
        const renderingOptions: ImageRenderingOptions = {
            alpha: this.env.alpha,
            checkerboardDensity: this.env.checkerboardDensity,
            viewMode: this.env.viewMode,
            colorMask: this.env.colorMask,
            secondaryColorMask: this.env.secondaryColorMask,
            ifPetImageFused: ifPetImageFused,
            petFusionColorMapTypeIdx: this.env.petFusionColorMapTypeIdx,
        };
        imageRenderer.render(
            this.parent.getView(),
            this.env.image,
            this.env.secondaryImage,
            renderingOptions
        );
    }

    /**
     * Optional method to perform a {@link postProcessing}-step provided by this {@link Filter}.
     * @param images An Array of {@link ImageObject Images} in the {@link Environment} that are to be rendered.
     */
    public postProcessing: ((images: ImageObject[]) => void) | undefined =
        undefined;
}

/**
 * Alpha applied to regular contours while a margin/ring preview
 * is active. Keeps contours visible but visually recedes them
 * so the preview stands out.
 */
const PREVIEW_CONTOUR_ALPHA = 0.3;

export class ContourFilter extends Filter<StructureSetObject> {
    public constructor(parent: Layer<StructureSetObject>, env: Environment) {
        super(parent, env);
    }

    public delete(): void {
        //the default contour filter has nothing to delete
    }

    public updateContent(structures: StructureSetObject[]): void {
        this.bindTexture(this.texture ? this.texture : this.parent.texture);
        gl.clear(gl.COLOR_BUFFER_BIT);
        const contourRenderer = RenderStorage.getRenderer(ContourRenderer);
        const options: ContourRenderOptions | undefined = this.env
            .operationPreviewStructure
            ? { alpha: PREVIEW_CONTOUR_ALPHA }
            : undefined;
        contourRenderer.render(this.parent.getView(), structures, options);
    }

    public postProcessing:
        | ((structures: StructureSetObject[]) => void)
        | undefined = undefined;
}

export class DoseFilter extends Filter<DoseDistribution> {
    public constructor(parent: Layer<DoseDistribution>, env: Environment) {
        super(parent, env);
    }

    public delete(): void {
        //the default contour filter has nothing to delete
    }

    public updateContent(doses: DoseDistribution[]): void {
        this.bindTexture(this.texture ? this.texture : this.parent.texture);
        gl.clear(gl.COLOR_BUFFER_BIT);
        const doseRenderer = RenderStorage.getRenderer(DoseRenderer);
        doseRenderer.render(this.parent.getView(), doses);
    }

    public postProcessing: ((doses: DoseDistribution[]) => void) | undefined =
        undefined;
}

/**
 * Type pointing to {@link Filter.constructor Filter's constructor}. Used for convenient {@link Filter} initialization.
 */
export type FilterClass<T> = new (
    parent: Layer<T>,
    env: Environment
) => Filter<T>;

/**
 * Method to construct a {@link Filter} from {@link FilterClass} with the provided {@link size}.
 * @param filterClass The {@link FilterClass} defining the type of {@link Filter} that is to be created.
 * @param size Size of the {@link Layer} requesting the {@link Filter} creation in pixels.\
 * This will be the size of the filter as well.
 * @returns An {@link Filter instance} of the provided {@link FilterClass}.
 */
export function newFilter<T>(
    filterClass: FilterClass<T>,
    parent: Layer<T>,
    env: Environment
): Filter<T> {
    return new filterClass(parent, env);
}
